// Single source of truth for where each onboarding-banner step takes the user.
//
// Today this routing is split between a hardcoded if/else in
// InvestorPortal.handleOnboardingStepClick and a separate ?tab= switch in AccountSettings.
// This registry consolidates it so the banner, the Settings list, and any future surface all
// resolve destinations the same way — add or move a step by editing one array, not chasing
// route strings across components.
//
// INERT until wired (see the banner-routing task): nothing imports this yet, so adding it
// changes no behavior.

import { TENANT } from '../config';
import { getIntroCallUrl } from './protectedDocuments';

// The four investor-onboarding steps, matching the ids produced by buildOnboardingFromUser.
export type OnboardingStepId =
  | 'create-account'
  | 'schedule-meeting'
  | 'verify-identity'
  | 'connect-funding';

export interface OnboardingStepConfig {
  id: OnboardingStepId;
  /**
   * Where clicking this step should take the user. A function so tenant/param logic stays
   * declarative and is evaluated at click time (not at module load).
   */
  destination: () => string;
}

/**
 * Declarative step → destination map. Steps route to the Settings section detail page where
 * they're completed (/portal/settings/:section) — except the intro call, which is its own
 * in-app page (`/portal/onboarding-call`, via getIntroCallUrl). Funding branches on the
 * tenant's crypto feature (wallet vs bank).
 */
export const ONBOARDING_STEPS: readonly OnboardingStepConfig[] = [
  { id: 'create-account', destination: () => '/portal/settings' },
  { id: 'schedule-meeting', destination: () => getIntroCallUrl('onboarding-banner') },
  { id: 'verify-identity', destination: () => '/portal/settings/kyc' },
  {
    id: 'connect-funding',
    destination: () =>
      TENANT.features.crypto ? '/portal/settings/wallet' : '/portal/settings/bank',
  },
];

const STEP_BY_ID: ReadonlyMap<string, OnboardingStepConfig> = new Map(
  ONBOARDING_STEPS.map((step) => [step.id, step]),
);

/**
 * Resolve where an onboarding step should navigate. Falls back to the Settings landing for
 * unknown ids so a step can never dead-end.
 */
export function stepToDestination(stepId: string): string {
  return STEP_BY_ID.get(stepId)?.destination() ?? '/portal/settings';
}
